import { currencyMinorDigits } from '@cf-shared/currency-minor-digits';
import { parsePrice } from '@cf-shared/shared-util';

/** What the Paddle rail knows about its two plans after the price preview (#6145). */
export interface YearlySavingInput {
  /** The monthly price in the currency's smallest unit (cents), from Paddle's `totals.total`. */
  monthlyMinor: number | undefined;
  /** The yearly price in the same unit. */
  yearlyMinor: number | undefined;
  /** The ISO currency code Paddle resolved for both plans. */
  currency: string | undefined;
  /** The yearly price exactly as the plan row shows it (Paddle's `formattedTotals.total`). */
  yearlyPriceText: string | undefined;
}

/** The two strings the Yearly plan row shows: the struck-through price and the badge amount. */
export interface YearlySaving {
  preDiscount: string;
  saving: string;
}

/**
 * Why no saving could be worked out. One value per point where {@link computeYearlySaving}
 * gives up, so a report says which check refused instead of only that one did.
 */
export enum YearlySavingRefusal {
  MissingAmount = 'missing-amount',
  AmountNotWhole = 'amount-not-whole',
  MissingCurrency = 'missing-currency',
  MissingPriceText = 'missing-price-text',
  NoSaving = 'no-saving',
  UnknownCurrency = 'unknown-currency',
  UnreadablePriceText = 'unreadable-price-text',
  DisplayHidesFraction = 'display-hides-fraction',
  SelfCheckMismatch = 'self-check-mismatch',
}

/** The saving to show, or the reason there is none. */
export type YearlySavingResult =
  | { saving: YearlySaving; refusal?: undefined }
  | { saving?: undefined; refusal: YearlySavingRefusal };

/** How a shown price is written: currency text around the number, and its two marks. */
interface PriceShape {
  prefix: string;
  suffix: string;
  decimalMark: string;
  /**
   * Undefined when the shown price has no mark at all and its currency has decimals, so
   * nothing says how a number of 1,000 or more is grouped (a price shown as "899 €").
   */
  thousandsMark: string | undefined;
  /** How many decimals the shown price writes. */
  fractionDigits: number;
  /**
   * How many decimals Paddle's amounts count in (the currency's ISO 4217 digits). It can be
   * more than the shown decimals: a forint price counted in hundredths may be shown as
   * "89 990 Ft" (df-c0c322359686).
   */
  amountDigits: number;
}

/**
 * The crossed-out "12 x Monthly" price and the "Save" amount for the Yearly plan row, or
 * undefined when there is nothing trustworthy to show.
 *
 * The sums are done in whole minor units, never on the shown text: the PayPro rail's old
 * text arithmetic dropped the thousands mark and printed cents for currencies that have none.
 * The results are then written in the same shape as the yearly price shown right beside them,
 * so the row never mixes "€229.99" with "275,88 €".
 *
 * A refusal means no badge. A wrong saving on the payment screen is worse than none, so every
 * doubt hides it — including the self-check that writing the yearly amount our way must give
 * back exactly the text Paddle shows. The caller decides whether a refusal needs reporting.
 */
export function computeYearlySaving(input: YearlySavingInput): YearlySavingResult {
  const { monthlyMinor, yearlyMinor, currency, yearlyPriceText } = input;
  if (monthlyMinor === undefined || yearlyMinor === undefined) return { refusal: YearlySavingRefusal.MissingAmount };
  if (!isWholeAmount(monthlyMinor) || !isWholeAmount(yearlyMinor)) return { refusal: YearlySavingRefusal.AmountNotWhole };
  if (!currency) return { refusal: YearlySavingRefusal.MissingCurrency };
  if (!yearlyPriceText) return { refusal: YearlySavingRefusal.MissingPriceText };

  const preDiscountMinor = monthlyMinor * 12;
  const savingMinor = preDiscountMinor - yearlyMinor;
  if (savingMinor <= 0) return { refusal: YearlySavingRefusal.NoSaving };

  // the decimals Paddle's amounts count in come from ISO 4217, never from Intl, whose answer
  // changes with the ICU version (#6950)
  const fractionDigits = currencyMinorDigits(currency);
  if (fractionDigits === undefined) return { refusal: YearlySavingRefusal.UnknownCurrency };
  const shape = readPriceShape(yearlyPriceText, fractionDigits);
  if (!shape) return { refusal: YearlySavingRefusal.UnreadablePriceText };

  // if the shown price drops decimals the amounts have, a fraction would be rounded away and
  // the saving could disagree with the prices beside it
  const shownUnit = 10 ** (shape.amountDigits - shape.fractionDigits);
  if (monthlyMinor % shownUnit !== 0 || yearlyMinor % shownUnit !== 0) return { refusal: YearlySavingRefusal.DisplayHidesFraction };

  // if the shown price gives no thousands mark, a crossed-out price of 1,000 or more would be
  // written with a guessed one that the self-check below cannot test
  if (shape.thousandsMark === undefined && preDiscountMinor >= 1000 * 10 ** shape.amountDigits) {
    return { refusal: YearlySavingRefusal.UnreadablePriceText };
  }

  // if our writing of the yearly amount does not reproduce Paddle's own text, our reading of
  // the shape (or of the amount) is wrong, and so would the two new strings be
  if (formatMinor(yearlyMinor, shape) !== yearlyPriceText) return { refusal: YearlySavingRefusal.SelfCheckMismatch };

  return {
    saving: {
      preDiscount: formatMinor(preDiscountMinor, shape),
      saving: formatMinor(savingMinor, shape),
    },
  };
}

/**
 * A plan's price per month, written the way its own price is shown (#6965): the sale
 * Yearly price of "$119.99" is "$10.00" a month. The total is divided by the months
 * and rounded to the decimals the shown price writes ($119.99 / 12 = $9.9992 → $10.00).
 *
 * Undefined whenever the shown price cannot be read back exactly, by the same checks
 * the yearly saving uses; the card then shows the sale price without the monthly line,
 * rather than a figure that might not match it.
 */
export function computePerMonthPrice(input: { totalMinor: number | undefined; currency: string | undefined; priceText: string | undefined; months: number }): string | undefined {
  const { totalMinor, currency, priceText, months } = input;
  if (totalMinor === undefined || !isWholeAmount(totalMinor) || !currency || !priceText || !(months > 0)) return undefined;
  const fractionDigits = currencyMinorDigits(currency);
  if (fractionDigits === undefined) return undefined;
  const shape = readPriceShape(priceText, fractionDigits);
  if (!shape) return undefined;
  // if our writing of the total does not reproduce the shown text, our reading of its shape is wrong
  if (formatMinor(totalMinor, shape) !== priceText) return undefined;
  // round to the smallest amount the shown price can write, so the figure has no hidden fraction
  const shownUnit = 10 ** (shape.amountDigits - shape.fractionDigits);
  const perMonthMinor = Math.round(totalMinor / months / shownUnit) * shownUnit;
  return formatMinor(perMonthMinor, shape);
}

/**
 * The shown price with every digit replaced by 9, for a report: it keeps the marks, the
 * currency text and the number of decimals, which is what tells why the text could not be
 * used, and drops the price itself.
 */
export function maskPriceDigits(text: string): string {
  return text.replace(/[0-9]/g, '9');
}

// a positive whole number of minor units, safe to multiply by 12
function isWholeAmount(value: number): boolean {
  return Number.isSafeInteger(value) && value > 0;
}

// a space, a no-break space or a narrow no-break space between digit groups, as in Hungarian
// "89 990,00 Ft" or Swedish "1 099,99 kr". The shared parsePrice reads only a comma or a full
// stop, so these are taken out before it reads the text and put back by formatMinor. The digit
// before the mark is captured rather than matched with a lookbehind, which the iOS 16.1-16.3 web
// view cannot load.
const SPACE_THOUSANDS_MARK = /([0-9])([ \u00A0\u202F])(?=[0-9]{3}(?![0-9]))/g;

// how the shown price is written, or undefined when it cannot be read
function readPriceShape(text: string, currencyDigits: number): PriceShape | undefined {
  const spaceMarks = new Set(Array.from(text.matchAll(SPACE_THOUSANDS_MARK), m => m[2]));
  // two different spaces as the mark is not a shape formatMinor can write back
  if (spaceMarks.size > 1) return undefined;
  const spaceMark = spaceMarks.values().next().value;
  const readable = spaceMark ? text.replace(SPACE_THOUSANDS_MARK, (_match, digitBefore: string) => digitBefore) : text;
  const parsed = parsePrice(readable);
  if (!parsed) return undefined;
  const prefix = parsed.currencyPre;
  const suffix = parsed.currencyPost;

  // a currency with no decimals has only one kind of mark, and it groups thousands. parsePrice
  // cannot tell which it saw ("¥30,000" reads as a decimal comma), so take whichever it found.
  if (currencyDigits === 0) {
    const thousandsMark = spaceMark || parsed.thousands || parsed.decimal || ',';
    return { prefix, suffix, decimalMark: '', thousandsMark, fractionDigits: 0, amountDigits: 0 };
  }

  // the digits after the last mark, which parsePrice took as the decimals
  const trailingDigits = parsed.decimal ? (/([0-9]*)[^0-9]*$/.exec(readable)?.[1].length ?? 0) : 0;
  // if the price shows no decimals for a currency that has them ("89 990 Ft"), or its one
  // mark is followed by three digits ("89.990 Ft") and so groups thousands, write whole units.
  // a currency with three decimals (KWD "12.500") keeps reading them as decimals.
  const groupsThousands = parsed.decimal !== '' && trailingDigits === 3 && currencyDigits !== 3 && !spaceMark
    && (!parsed.thousands || parsed.thousands === parsed.decimal);
  if (!parsed.decimal || groupsThousands) {
    const thousandsMark = spaceMark || parsed.thousands || parsed.decimal || undefined;
    return { prefix, suffix, decimalMark: '', thousandsMark, fractionDigits: 0, amountDigits: currencyDigits };
  }

  const decimalMark = parsed.decimal;
  // below 1,000 the shown price carries no thousands mark, so assume the other one
  const thousandsMark = spaceMark || parsed.thousands || (decimalMark === ',' ? '.' : ',');
  return { prefix, suffix, decimalMark, thousandsMark, fractionDigits: currencyDigits, amountDigits: currencyDigits };
}

// write an amount in minor units the way the shown price is written. the caller has checked
// that the amount holds no fraction the shown decimals would drop.
function formatMinor(minor: number, shape: PriceShape): string {
  const shown = minor / 10 ** (shape.amountDigits - shape.fractionDigits);
  const divisor = 10 ** shape.fractionDigits;
  const whole = Math.floor(shown / divisor);
  const fraction = shown % divisor;
  const groupedWhole = String(whole).replace(/\B(?=(\d{3})+(?!\d))/g, shape.thousandsMark ?? '');
  const fractionText = shape.fractionDigits > 0
    ? shape.decimalMark + String(fraction).padStart(shape.fractionDigits, '0')
    : '';
  return `${shape.prefix}${groupedWhole}${fractionText}${shape.suffix}`;
}
